import {Tooltip} from "primereact/tooltip";
import {useId} from "react";
import {useTranslation} from "react-i18next";

import {
    buildAddressMapUrl,
    buildCoordinateMapUrl,
    EXTERNAL_MAP_PROVIDERS,
    type ExternalMapProvider,
} from "../../util/externalMapLinks";

/** Icon je Kartendienst. */
const PROVIDER_ICONS: Record<ExternalMapProvider, string> = {
    google: "pi pi-google",
    bing: "pi pi-microsoft",
    osm: "pi pi-globe",
};

interface MapLinksProps {
    /** ob die Adresse oder die Koordinate an den Kartendienst übergeben wird */
    mode: "address" | "coordinate";
    /** Suchbegriff (nur bei mode="address") */
    query?: string;
    /** Breitengrad (nur bei mode="coordinate") */
    lat?: number | null;
    /** Längengrad (nur bei mode="coordinate") */
    lon?: number | null;
    /** optionale zusätzliche CSS-Klassen */
    className?: string;
}

/**
 * Absprung-Icons zu Google Maps, Bing Maps und OpenStreetMap (#19281) — je Icon ein Link, der die
 * Adresse bzw. die Koordinate als Parameter mitgibt und in einem neuen Tab öffnet.
 */
export default function MapLinks({mode, query, lat, lon, className}: MapLinksProps) {
    const {t} = useTranslation();
    // useId() enthält Doppelpunkte und ist damit als CSS-Selektor unbrauchbar -> entfernen.
    const tooltipScopeId = useId().replace(/:/g, "");
    const scopeClass = `MapLinks-${tooltipScopeId}`;

    const links = EXTERNAL_MAP_PROVIDERS.flatMap((provider) => {
        const url =
            mode === "address" ? buildAddressMapUrl(provider, query ?? "") : buildCoordinateMapUrl(provider, lat, lon);
        return url ? [{provider, url}] : [];
    });

    if (links.length === 0) return null;

    return (
        <span className={`MapLinks ${scopeClass} inline-flex gap-2 align-middle${className ? ` ${className}` : ""}`}>
            <Tooltip target={`.${scopeClass} > a`} position="top" className="custom-tooltip" />
            {links.map(({provider, url}) => (
                <a
                    key={provider}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    // Die Adresskachel ist selbst klickbar (Adressauswahl) - Klick darf nicht durchschlagen.
                    onClick={(event) => event.stopPropagation()}
                    className="text-blue-500 hover:text-blue-700 no-underline"
                    aria-label={t(`address.mapLinks.${mode}.${provider}`)}
                    data-pr-tooltip={t(`address.mapLinks.${mode}.${provider}`)}
                    test-key={`map-link-${mode}-${provider}`}
                >
                    <span className={PROVIDER_ICONS[provider]} />
                </a>
            ))}
        </span>
    );
}
